import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import surveyApi from "../../api/survey/survey";
import { ui } from "../../helpers/ui";

export interface SurveyRating {
  itemKey: string;
  label: string;
  score: number | null;
  comment: string | null;
  /**
   * Broj treninga klijenta: za pitanja o treneru broj treninga kod tog
   * trenera, za opšta pitanja ukupan broj treninga. null kad je nepoznato.
   */
  trainingsCount: number | null;
}

export interface SurveyResult {
  id: number;
  date: string;
  anonymous: boolean;
  suggestions: string | null;
  clientId: number | null;
  clientName: string | null;
  totalTrainings: number;
  ratings: SurveyRating[];
}

export interface ItemEntry {
  score: number | null;
  comment: string | null;
  clientName: string | null;
  anonymous: boolean;
  trainingsCount: number | null;
}

export interface ItemSummary {
  itemKey: string;
  label: string;
  average: number | null;
  count: number;
  /** Jedan red po odgovoru: ocena i njen komentar zajedno */
  entries: ItemEntry[];
}

/** "1 trening", "2 treninga", "5 treninga" (srpska množina) */
export const trainingsWord = (count: number): string => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return "trening";
  return "treninga";
};

const InitSurveyResultsLogic = () => {
  const navigate = useNavigate();

  const [surveys, setSurveys] = useState<SurveyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"users" | "items">("users");

  // Isključivanje ocena klijenata sa malim brojem treninga. Za pitanja o
  // treneru gleda se broj treninga kod tog trenera, za opšta ukupan broj.
  const [excludeEnabled, setExcludeEnabled] = useState(false);
  const [excludeMax, setExcludeMax] = useState(1);

  useEffect(() => {
    surveyApi
      .getReport()
      .then((response) => setSurveys(response.data ?? []))
      .catch((e) => {
        ui.alert(t(e.response?.data?.error?.message || "Greška"));
      })
      .finally(() => setLoading(false));
  }, []);

  const isRatingExcluded = (rating: SurveyRating): boolean => {
    if (!excludeEnabled) return false;
    if (rating.trainingsCount === null) return false;
    return rating.trainingsCount <= excludeMax;
  };

  /** Upitnici sa ocenama koje prolaze filter po broju treninga. */
  const filteredSurveys = (): SurveyResult[] =>
    surveys.map((survey) => ({
      ...survey,
      ratings: survey.ratings.filter((rating) => !isRatingExcluded(rating)),
    }));

  /**
   * Grupiše odgovore po stavci. Redosled stavki prati redosled iz upitnika -
   * uzima se iz prvog odgovora koji tu stavku sadrži, pa se ne oslanja na
   * trenutnu konfiguraciju (stavka može biti uklonjena, a stari odgovori ostaju).
   */
  const byItem = (): ItemSummary[] => {
    const map = new Map<string, ItemSummary>();

    // Redosled i naslovi stavki se uzimaju iz NEfiltriranih odgovora, da se
    // kartice ne preuređuju i ne nestaju kad se uključi filter po broju
    // treninga - stavka bez preostalih ocena prikaže "nema ocena".
    for (const survey of surveys) {
      for (const rating of survey.ratings) {
        if (!map.has(rating.itemKey)) {
          map.set(rating.itemKey, {
            itemKey: rating.itemKey,
            label: rating.label,
            average: null,
            count: 0,
            entries: [],
          });
        }
      }
    }

    for (const survey of filteredSurveys()) {
      for (const rating of survey.ratings) {
        let entry = map.get(rating.itemKey);
        if (!entry) {
          entry = {
            itemKey: rating.itemKey,
            label: rating.label,
            average: null,
            count: 0,
            entries: [],
          };
          map.set(rating.itemKey, entry);
        }
        if (
          (rating.score !== null && rating.score !== undefined) ||
          rating.comment
        ) {
          entry.entries.push({
            score: rating.score ?? null,
            comment: rating.comment ?? null,
            clientName: survey.clientName,
            anonymous: survey.anonymous,
            trainingsCount: rating.trainingsCount,
          });
        }
      }
    }

    const items = Array.from(map.values());
    for (const item of items) {
      const scored = item.entries.filter((e) => e.score !== null);
      item.count = scored.length;
      item.average =
        scored.length > 0
          ? scored.reduce((sum, e) => sum + (e.score as number), 0) /
            scored.length
          : null;
      // Ocene rastuce, odgovori samo sa komentarom (bez ocene) na kraju
      item.entries.sort((a, b) => {
        if (a.score === null && b.score === null) return 0;
        if (a.score === null) return 1;
        if (b.score === null) return -1;
        return a.score - b.score;
      });
    }

    return items;
  };

  const suggestions = () =>
    surveys
      .filter((s) => s.suggestions)
      .map((s) => ({
        clientName: s.clientName,
        anonymous: s.anonymous,
        text: s.suggestions as string,
      }));

  const onBack = () => navigate("/home");

  return {
    surveys,
    filteredSurveys,
    loading,
    view,
    setView,
    excludeEnabled,
    setExcludeEnabled,
    excludeMax,
    setExcludeMax,
    byItem,
    suggestions,
    onBack,
  };
};

export default InitSurveyResultsLogic;
